/* Imports needed for all question form subclasses. */
import * as React from 'react';
import { connect } from 'react-redux';
import * as classnames from 'classnames';
import {
    QuestionFormBase,
    QuestionFormBaseProps,
    QuestionFormBaseState,
    mapStateToQuestionFormBaseProps,
    mapDispatchToQuestionFormBaseProps
} from '../../QuestionFormBase';
import * as _ from 'lodash';
import {
    MULTIMEDIA_TYPES,
    OpenEndedSettings,
    QTYPES
} from '@nexxt/common/types';
import NotifyHOC from '../../../../../../HOC/Notifications';
import { formatNumber } from '@nexxt/common/services/TextService';

/* Components used in this question form. */
import BuilderHeader from '../../../common/builderHeader';
import QuestionSettings from '../../../common/questionSettings';

/* Imports specifically for open-end question form. */
import MultimediaBubble from '../../../common/MultimediaBubble';

type Props = QuestionFormBaseProps;

interface States extends QuestionFormBaseState {
    showFormattedPlaceholder: boolean;
}

class Numeric extends QuestionFormBase<Props, States> {
    constructor(p: QuestionFormBaseProps) {
        super(p, QTYPES.NUMERIC);
        const { isNewQuestion, currentProject } = this.props;
        const languages = currentProject.languages;
        const question = {
            ...this.state.question,
            showContinueButton: isNewQuestion
                ? true
                : this.state.question.showContinueButton,
            allowUserEdit: false,
            openEndedSettings: isNewQuestion
                ? this.getInitialOpenEndedSettings()
                : this.state.question.openEndedSettings
        };
        this.state = {
            ...this.state,
            question,
            showFormattedPlaceholder: true
        };
    }

    /**
     * get initial open ended settings for numeric question type
     * @returns OpenEndSettings
     */
    getInitialOpenEndedSettings(): OpenEndedSettings {
        return {
            showMin: false,
            showMax: false,
            allowDecimal: false,
            numDecimals: 0,
            thousandSeparator: false,
            leftUnit: '',
            rightUnit: '',
            placeholder: 0
        };
    }

    /**
     * Update question.openEndedSettings in react state
     */
    setOpenEndSettings(keyName: string, value: any) {
        this.setState(
            (prevState) => ({
                ...prevState,
                question: {
                    ...prevState.question,
                    openEndedSettings: {
                        ...prevState.question.openEndedSettings,
                        [keyName]: value
                    }
                }
            }),
            this.setEditFlag
        );
    }

    render() {
        const { currentProject } = this.props;
        const { question, isSettingBoxLayoutOpen, showFormattedPlaceholder } =
            this.state;
        const questionError =
            (this.state.showError && this.state.questionError) || {};
        const placeholderLength =
            String(question.openEndedSettings?.placeholder)?.length > 20
                ? String(question.openEndedSettings?.placeholder)?.length
                : 20;
        const formattedPlaceholder = formatNumber(
            question.openEndedSettings?.placeholder,
            {
                thousandSeparator:
                    question.openEndedSettings?.thousandSeparator,
                allowDecimal: question.openEndedSettings?.allowDecimal,
                numDecimals: question.openEndedSettings?.numDecimals
            }
        );
        const leftUnitLength =
            question.openEndedSettings?.leftUnit?.length < 7
                ? 7
                : question.openEndedSettings?.leftUnit?.length;
        const rightUnitLength =
            question.openEndedSettings?.rightUnit?.length < 7
                ? 7
                : question.openEndedSettings?.rightUnit?.length;
        return (
            <div>
                <div className="project-add--question-builder">
                    <BuilderHeader
                        toggleSettingBoxLayout={this.toggleSettingBoxLayout}
                        isSettingBoxLayoutOpen={isSettingBoxLayoutOpen}
                        showAlert={!this.props.editEnabled}
                        alertMessages={this.getWarningMessage()}
                        question={question}
                        disabled={!this.props.editEnabled}
                        setValue={this.setQuestion}
                        errorObject={questionError}
                    />
                    <div className="builder-body">
                        {(isSettingBoxLayoutOpen && (
                            <QuestionSettings
                                onClose={this.toggleSettingBoxLayout}
                                question={question}
                                questionSettings={{
                                    allowUserEdit: true,
                                    showNotApplicableOption: true,
                                    allowDecimal: true,
                                    showMin: true,
                                    showMax: true,
                                    useThousandSeparator: true
                                }}
                                disableQuestionSettings={{
                                    allowUserEdit: true
                                }}
                                errorObject={questionError}
                                setValue={this.setQuestion}
                                disabled={!this.props.editEnabled}
                            />
                        )) ||
                            null}
                        {this.renderErrorNotes()}
                        <MultimediaBubble
                            question={question}
                            languages={currentProject.languages}
                            setValue={this.setQuestion}
                            setValueWithLanguage={
                                this.NEW_setQuestionTextTranslation
                            }
                            addNewBubble={this.NEW_addNewEditor}
                            removeBubble={this.NEW_removeEditor}
                            disabled={!this.props.editEnabled}
                            questionError={questionError}
                            settings={{ showClose: true }}
                        />
                        <div className="col-md-12 form-group">
                            <div
                                className={classnames('form-group-header', {
                                    error:
                                        questionError &&
                                        questionError.iconNumber
                                })}
                            >
                                Numeric Format
                            </div>
                        </div>
                        <section
                            className="form-group flex"
                            style={{
                                justifyContent: 'center'
                            }}
                        >
                            <div
                                className={classnames('form-group__numeric', {
                                    error: questionError?.leftUnit
                                })}
                            >
                                <input
                                    className={
                                        'form-group__underline unit right-align'
                                    }
                                    style={{
                                        width: `${leftUnitLength}ch`
                                    }}
                                    placeholder="$"
                                    onChange={(e) =>
                                        this.setOpenEndSettings(
                                            'leftUnit',
                                            e.target.value
                                        )
                                    }
                                    value={question.openEndedSettings?.leftUnit}
                                    disabled={!this.props.editEnabled}
                                />
                                <span className="form-group__numeric__label">
                                    Left Unit
                                </span>
                                <span className="form-group__numeric__sublabel">
                                    (Optional)
                                </span>
                            </div>
                            <div
                                className="form-group__numeric"
                                style={{
                                    maxWidth: `calc(100% - 250px - ${
                                        leftUnitLength + rightUnitLength
                                    }ch)`,
                                    overflow: 'hidden'
                                }}
                            >
                                {showFormattedPlaceholder ? (
                                    <div
                                        className="form-group__underline right-align"
                                        onClick={() =>
                                            this.setState({
                                                showFormattedPlaceholder: false
                                            })
                                        }
                                        style={{
                                            width: `${
                                                placeholderLength -
                                                leftUnitLength +
                                                rightUnitLength
                                            }ch`,
                                            minWidth: '20ch',
                                            maxWidth: '100%'
                                        }}
                                    >
                                        {formattedPlaceholder}
                                    </div>
                                ) : (
                                    <input
                                        className="form-group__underline right-align"
                                        placeholder={formattedPlaceholder}
                                        style={{
                                            width: `${
                                                placeholderLength -
                                                leftUnitLength +
                                                rightUnitLength
                                            }ch`,
                                            minWidth: '20ch',
                                            maxWidth: '100%'
                                        }}
                                        onFocus={() =>
                                            this.setState({
                                                showFormattedPlaceholder: false
                                            })
                                        }
                                        onBlur={() =>
                                            this.setState({
                                                showFormattedPlaceholder: true
                                            })
                                        }
                                        onChange={(e) => {
                                            this.setOpenEndSettings(
                                                'placeholder',
                                                e.target.value
                                            );
                                        }}
                                        value={
                                            question.openEndedSettings
                                                ?.placeholder
                                        }
                                        autoFocus
                                        disabled={!this.props.editEnabled}
                                        type="number"
                                    />
                                )}
                                <span className="form-group__numeric__label">
                                    Placeholder
                                </span>
                                <span className="form-group__numeric__sublabel"></span>
                            </div>
                            <div
                                className={classnames('form-group__numeric', {
                                    error: questionError?.rightUnit
                                })}
                            >
                                <input
                                    className="form-group__underline unit"
                                    placeholder="%"
                                    style={{
                                        width: `${rightUnitLength}ch`
                                    }}
                                    onChange={(e) =>
                                        this.setOpenEndSettings(
                                            'rightUnit',
                                            e.target.value
                                        )
                                    }
                                    disabled={!this.props.editEnabled}
                                    value={
                                        question.openEndedSettings?.rightUnit
                                    }
                                />
                                <span className="form-group__numeric__label">
                                    Right Unit
                                </span>
                                <span className="form-group__numeric__sublabel">
                                    (Optional)
                                </span>
                            </div>
                        </section>
                        <footer className="form-group__numeric-error-msg">
                            {questionError?.leftUnit ||
                                questionError?.rightUnit}
                        </footer>
                    </div>
                    {/* end of builder-body*/}
                    {question.showNotApplicableOption ? (
                        <MultimediaBubble
                            languages={currentProject.languages}
                            question={question}
                            setValue={this.setQuestion}
                            setValueWithLanguage={
                                this.NEW_setQuestionTextTranslation
                            }
                            questionError={questionError}
                            keyName="notApplicableTexts"
                            disabled={!this.props.editEnabled}
                            disableAddBubble
                            defaultTextType={MULTIMEDIA_TYPES.TEXT}
                            defaultText="BUTTON.NOT_APPLICABLE"
                            questionText="Not Applicable text"
                            settings={{
                                noBorder: true,
                                useTranslation: true
                            }}
                        />
                    ) : null}
                    {question.showContinueButton ? (
                        <MultimediaBubble
                            languages={currentProject.languages}
                            question={question}
                            setValue={this.setQuestion}
                            setValueWithLanguage={
                                this.NEW_setQuestionTextTranslation
                            }
                            questionError={questionError}
                            keyName="continueTexts"
                            disabled={!this.props.editEnabled}
                            disableAddBubble
                            defaultTextType={MULTIMEDIA_TYPES.TEXT}
                            defaultText="BUTTON.CONTINUE"
                            questionText="Continue button text"
                            settings={{
                                noBorder: true,
                                useTranslation: true
                            }}
                        />
                    ) : null}
                </div>
            </div>
        );
    }
}

export default NotifyHOC(
    connect(
        mapStateToQuestionFormBaseProps,
        mapDispatchToQuestionFormBaseProps
    )(Numeric)
);
